import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { LifeBuoy, Phone, MessageCircle, Globe, Heart, ShieldAlert, Users, AlertTriangle } from 'lucide-react';
import { api } from '../utils/api';

const ICONS = { LifeBuoy, Phone, MessageCircle, Globe, Heart, ShieldAlert, Users, AlertTriangle };

/**
 * Public /resources page — admin-editable directory of hotlines and support lines.
 * Fetches hero, emergency banner, sections + items from /api/resources.
 */
export default function Resources() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/resources');
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError('Could not load resources right now — please try again in a moment.');
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!data && !error) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center" style={{ fontFamily: 'Lora, serif', color: '#8C867D' }}>
          Loading resources…
        </div>
        <Footer />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center" style={{ fontFamily: 'Lora, serif', color: '#8C5E5E' }} data-testid="resources-error">
          {error}
        </div>
        <Footer />
      </div>
    );
  }

  const { page, sections } = data;

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      {/* Hero with soft dawn background image */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?w=1600&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
          aria-hidden
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(253,251,247,0.92) 0%, rgba(243,239,231,0.88) 55%, rgba(253,251,247,0.85) 100%)' }} aria-hidden />
        <div className="max-w-4xl mx-auto px-6 pt-20 pb-14 relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: '#FFFFFFAA', border: '1px solid #E8C26555', backdropFilter: 'blur(8px)' }}>
            <LifeBuoy className="w-4 h-4" style={{ color: '#B8891A' }} />
            <span className="text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#8B6A0A', fontWeight: 700, letterSpacing: '0.03em' }}>{page.hero_badge_label}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', lineHeight: 1.15 }} data-testid="resources-hero-title">
            {page.hero_title}
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ fontFamily: 'Lora, serif', color: '#4A423B', fontStyle: 'italic', lineHeight: 1.75 }}>
            {page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Emergency alert */}
      <section className="max-w-4xl mx-auto px-6 pb-6 -mt-4 relative">
        <div className="p-6 rounded-3xl flex items-start gap-4" style={{ background: 'linear-gradient(135deg, #FFF5E5 0%, #FFEEDD 100%)', border: '1px solid #E8C26580', boxShadow: '0 12px 40px rgba(229,122,68,0.10)' }} data-testid="resources-emergency">
          <div className="p-2.5 rounded-2xl flex-shrink-0" style={{ backgroundColor: '#E57A44', color: '#FFFFFF' }}>
            <AlertTriangle className="w-6 h-6" strokeWidth={2} />
          </div>
          <div>
            <p className="font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: '18px' }}>{page.emergency_title}</p>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {page.emergency_body}
            </p>
          </div>
        </div>
      </section>

      {/* Quick jump-nav — table of contents */}
      {sections.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 pt-6 pb-2" data-testid="resources-jump-nav">
          <p className="text-xs uppercase tracking-widest mb-3 text-center" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D', letterSpacing: '0.15em' }}>Jump to a section</p>
          <div className="flex flex-wrap justify-center gap-2">
            {sections.map((s) => {
              const IconComp = ICONS[s.icon_key] || Heart;
              return (
                <a
                  key={s.id}
                  href={`#section-${s.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition hover:-translate-y-0.5"
                  style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: `${s.tint}15`, color: s.tint, border: `1px solid ${s.tint}40` }}
                  data-testid={`jump-${s.id}`}
                >
                  <IconComp className="w-3.5 h-3.5" />
                  <span>{s.title}</span>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Dynamic sections */}
      {sections.map((section) => (
        <section id={`section-${section.id}`} key={section.id} className="max-w-4xl mx-auto px-6 py-8 scroll-mt-24" data-testid={`resources-section-${section.id}`}>
          <SectionHeader iconKey={section.icon_key} tint={section.tint} title={section.title} count={section.items.length} />
          {section.items.length === 0 ? (
            <p className="text-sm italic" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
              No resources yet in this section.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.items.map((it) => (
                <ResourceCard key={it.id} title={it.title} body={it.body} actions={it.actions || []} tint={section.tint} testid={`res-${it.id}`} />
              ))}
            </div>
          )}
        </section>
      ))}

      {/* Faith-based caveat */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="p-6 rounded-3xl" style={{ background: '#F3EFE7', border: '1px solid #E6E2D8' }}>
          <div className="flex items-start gap-4">
            <MessageCircle className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#78A18E' }} />
            <div>
              <p className="font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>A gentle reminder</p>
              <p className="text-sm mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>
                Love Restored offers faith-based encouragement and pastoral-style reflection. It is not a substitute for therapy, medical advice, crisis intervention, or emergency services. Please use the lines above whenever you or someone you love needs trained professional support.
              </p>
              <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                See our <Link to="/terms" style={{ color: '#E57A44', fontWeight: 600 }}>Terms</Link> and <Link to="/privacy" style={{ color: '#E57A44', fontWeight: 600 }}>Privacy</Link> pages for more.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SectionHeader({ iconKey, tint, title, count }) {
  const IconComp = ICONS[iconKey] || Heart;
  return (
    <div className="mb-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${tint}18`, color: tint, border: `1px solid ${tint}30` }}>
          <IconComp className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h2>
          {typeof count === 'number' && (
            <p className="text-xs mt-0.5" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D', letterSpacing: '0.05em' }}>
              {count} {count === 1 ? 'resource' : 'resources'}
            </p>
          )}
        </div>
      </div>
      <div style={{ height: 1, background: `linear-gradient(to right, ${tint}40, transparent)`, marginTop: 4 }} />
    </div>
  );
}

function ResourceCard({ title, body, actions = [], tint = '#E57A44', testid }) {
  return (
    <div
      className="p-5 rounded-2xl transition-all duration-300 hover:-translate-y-1"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #F0EBE0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        borderLeft: `4px solid ${tint}`,
      }}
      data-testid={testid}
    >
      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h3>
      <p className="text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>{body}</p>
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((a, i) => {
            const isExternal = a.href.startsWith('http');
            return (
              <a
                key={i}
                href={a.href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition hover:-translate-y-0.5"
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  backgroundColor: i === 0 ? tint : '#F3EFE7',
                  color: i === 0 ? '#FFFFFF' : '#2C2724',
                  border: i === 0 ? 'none' : '1px solid #E6E2D8',
                  boxShadow: i === 0 ? `0 3px 10px ${tint}45` : 'none',
                }}
              >
                {a.label}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
